/**
 * ProjectLocalesContext
 *
 * Provides project locale configuration to editor components.
 * Used by locale selectors, StringDefaultEditor, and canvas locale pickers.
 *
 * When no provider exists, consumers get default (en-US only).
 *
 * @module locale-config/context
 */

import { createContext, useContext, useMemo } from 'react';
import type { ReactNode } from 'react';
import type { ProjectLocale } from '../types/localeConfig.types';
import { DEFAULT_LOCALE_CONFIG } from '../types/localeConfig.types';
import { useLocaleConfig } from '../hooks/useLocaleConfig';

export interface ProjectLocalesContextValue {
  /** Ordered list of project locales */
  locales: ProjectLocale[];
  /** Just the locale codes (convenience for dropdowns) */
  localeCodes: string[];
  /** Default locale code */
  defaultLocale: string;
  /** Whether config is still loading */
  loading: boolean;
  /** Whether the canonical project locale document has been saved. */
  configured: boolean;
}

const defaultValue: ProjectLocalesContextValue = {
  locales: DEFAULT_LOCALE_CONFIG.locales,
  localeCodes: [DEFAULT_LOCALE_CONFIG.defaultLocale],
  defaultLocale: DEFAULT_LOCALE_CONFIG.defaultLocale,
  loading: false,
  configured: false,
};

const ProjectLocalesContext = createContext<ProjectLocalesContextValue>(defaultValue);

export interface ProjectLocalesProviderProps {
  children: ReactNode;
  projectId: string | undefined;
}

/**
 * Provides project locales from Firestore config.
 * Wrap around editor component trees that need locale information.
 */
export function ProjectLocalesProvider({ children, projectId }: ProjectLocalesProviderProps) {
  const { config, loading, configured } = useLocaleConfig(projectId);

  const value = useMemo<ProjectLocalesContextValue>(() => ({
    locales: config.locales,
    localeCodes: config.locales.map(l => l.code),
    defaultLocale: config.defaultLocale,
    loading,
    configured,
  }), [config.locales, config.defaultLocale, configured, loading]);

  return (
    <ProjectLocalesContext.Provider value={value}>
      {children}
    </ProjectLocalesContext.Provider>
  );
}

/**
 * Get project locale configuration.
 * Returns default (en-US) when no ProjectLocalesProvider is present.
 */
export function useProjectLocales(): ProjectLocalesContextValue {
  return useContext(ProjectLocalesContext);
}
